import { forwardRef } from 'react';
import { Box, BoxProps } from './box';

/**
 * Main component - Semantic wrapper for main content areas.
 *
 * Renders as an HTML `<main>` element for accessibility and SEO.
 * Used in mobile app for primary content sections.
 *
 * The `<main>` element represents the dominant content of the page.
 * There should only be one main element per page, and it should not be
 * nested within <article>, <aside>, <footer>, <header>, or <nav> elements.
 *
 * @example
 * // App layout
 * <Box>
 *   <Header />
 *   <Main flexGrow={1} p={2}>
 *     <Routes />
 *   </Main>
 *   <Footer />
 * </Box>
 *
 * @example
 * // With flex layout
 * <Main display="flex" flexDirection="column" minHeight="100vh">
 *   <Typography variant="h1">Page Title</Typography>
 *   <Box flexGrow={1}>
 *     Content
 *   </Box>
 * </Main>
 */
export type MainProps = Omit<BoxProps, 'component'>;

export const Main = forwardRef<HTMLElement, MainProps>((props, ref) => (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  <Box ref={ref as any} component="main" {...props} />
));

Main.displayName = 'Main';
